import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import {
  Plus,
  Edit,
  Save,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  GripVertical
} from 'lucide-react'

import ExerciseCard from './ExerciseCard'

const PlanSection = ({ section, planId, onOpenExerciseLibrary }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [sectionName, setSectionName] = useState(section.name)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const { updateSection, deleteSection, addExerciseToSection } = useExercisePlanStore()

  const { setNodeRef, isOver } = useDroppable({
    id: `section-${section.id}`,
  })

  const handleSaveName = () => {
    updateSection(planId, section.id, { name: sectionName })
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setSectionName(section.name)
    setIsEditing(false)
  }

  const handleDeleteSection = () => {
    if (window.confirm(`Are you sure you want to delete the "${section.name}" section?`)) {
      deleteSection(planId, section.id)
    }
  }

  const handleAddExercise = () => {
    onOpenExerciseLibrary()
  }

  const getSectionIcon = (type) => {
    switch (type) {
      case 'warmup':
        return '🔥'
      case 'main':
        return '💪'
      case 'cooldown':
        return '🧘'
      default:
        return '⚡'
    }
  }

  const getSectionColor = (type) => {
    switch (type) {
      case 'warmup':
        return 'border-orange-200 bg-orange-50'
      case 'main':
        return 'border-blue-200 bg-blue-50'
      case 'cooldown':
        return 'border-green-200 bg-green-50'
      default:
        return 'border-purple-200 bg-purple-50'
    }
  }

  return (
    <Card className={`${getSectionColor(section.type)} ${isOver ? 'ring-2 ring-fitness-primary-light' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getSectionIcon(section.type)}</span>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  className="h-8"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
                />
                <Button size="sm" onClick={handleSaveName} variant="ghost">
                  <Save className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={handleCancelEdit} variant="ghost">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">{section.name}</CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  className="h-6 w-6 p-0"
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              {section.exercises.length} exercise{section.exercises.length !== 1 ? 's' : ''}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-6 w-6 p-0"
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
            {section.type === 'custom' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDeleteSection}
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pt-0" ref={setNodeRef}>
              <SortableContext
                items={section.exercises.map(ex => ex.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {section.exercises.map((exercise, index) => (
                    <ExerciseCard
                      key={exercise.id}
                      exercise={exercise}
                      planId={planId}
                      sectionId={section.id}
                      index={index}
                    />
                  ))}
                  
                  {/* Add Exercise Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors"
                  >
                    <Button
                      variant="ghost"
                      onClick={handleAddExercise}
                      className="w-full text-slate-600 hover:text-slate-900"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Exercise
                    </Button>
                  </motion.div>
                </div>
              </SortableContext>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  )
}

export default PlanSection